import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  Star,
  CalendarToday,
  Repeat,
  Schedule,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import {
  formatHours,
  formatOperationalDays,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  MotionList,
  MotionItem,
  AnimatedNumber,
  Reveal,
} from './themeHelpers';

const MotionBox = motion(Box);

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function VibrantTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const shouldReduce = useReducedMotion();

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `Welcome to ${library.name}! Your ultimate study destination with modern facilities and vibrant atmosphere.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  // Generate gradient from accent color
  const gradient = `linear-gradient(135deg, ${accentColor} 0%, #8B5CF6 50%, #EC4899 100%)`;

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);

  // Stat 2: available_seats (if present), else capacity
  const availSeats = library.available_seats ?? library.capacity;

  // Stat 3: locker_count when >0, else amenities count
  const showLockers = library.locker_count > 0;

  // AnimatedNumber candidates
  const capacityNum = typeof library.capacity === 'number' && Number.isFinite(library.capacity) ? library.capacity : null;
  const availSeatsNum = typeof availSeats === 'number' && Number.isFinite(availSeats) ? availSeats : null;
  const amenitiesNum = typeof library.amenities?.length === 'number' ? (library.amenities.length || 5) : 5;
  const lockerNum = showLockers && typeof library.locker_count === 'number' ? library.locker_count : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0F0F1A' }}>
      {/* Hero Section with gradient overlay */}
      <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, rgba(139,92,246,0.8) 0%, rgba(236,72,153,0.6) 50%, rgba(15,15,26,0.9) 100%)`,
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Hero text — existing animation block, kept intact + enhanced */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            width: '90%',
            maxWidth: 800,
          }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              icon={<Star sx={{ color: 'white !important' }} />}
              label="PREMIUM STUDY SPACE"
              sx={{
                mb: 3,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4.5rem' },
                mb: 2,
                textShadow: '0 4px 30px rgba(0,0,0,0.3)',
              }}
            >
              {library.name}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
              <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
              {library.address}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => onBookNow()}
              sx={{
                background: 'white',
                color: '#8B5CF6',
                fontWeight: 700,
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                borderRadius: 50,
                transition: 'all 0.3s',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 10px 32px rgba(139,92,246,0.35)',
                },
              }}
            >
              Book Your Spot
            </Button>
          </MotionBox>
        </Box>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          {shouldReduce ? (
            <Box
              sx={{
                width: 30,
                height: 50,
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: 15,
                display: 'flex',
                justifyContent: 'center',
                pt: 1,
              }}
            >
              <Box sx={{ width: 4, height: 12, bgcolor: 'white', borderRadius: 2 }} />
            </Box>
          ) : (
            <MotionBox
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              sx={{
                width: 30,
                height: 50,
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: 15,
                display: 'flex',
                justifyContent: 'center',
                pt: 1,
              }}
            >
              <Box sx={{ width: 4, height: 12, bgcolor: 'white', borderRadius: 2 }} />
            </MotionBox>
          )}
        </Box>
      </Box>

      {/* Stats Section — animated count-ups */}
      <Box sx={{ background: gradient, py: 6 }}>
        <Container maxWidth="lg">
          <MotionList
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${showLockers || availSeatsNum !== null ? 4 : 3}, 1fr)`,
              gap: 24,
            }}
          >
            {/* Total seats */}
            {capacityNum !== null && (
              <MotionItem>
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Typography variant="h2" fontWeight={800}>
                    <AnimatedNumber value={capacityNum} duration={1.4} />
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.8 }}>Total Seats</Typography>
                </Box>
              </MotionItem>
            )}
            {capacityNum === null && (
              <MotionItem>
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Typography variant="h2" fontWeight={800}>{library.capacity || '—'}</Typography>
                  <Typography variant="h6" sx={{ opacity: 0.8 }}>Total Seats</Typography>
                </Box>
              </MotionItem>
            )}

            {/* Available seats */}
            {availSeats !== null && availSeats !== undefined && (
              <MotionItem>
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Typography variant="h2" fontWeight={800}>
                    {availSeatsNum !== null ? (
                      <AnimatedNumber value={availSeatsNum} duration={1.4} />
                    ) : (
                      availSeats
                    )}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.8 }}>Seats Free</Typography>
                </Box>
              </MotionItem>
            )}

            {/* Amenities count */}
            <MotionItem>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h2" fontWeight={800}>
                  <AnimatedNumber value={amenitiesNum} duration={1.2} suffix="+" />
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8 }}>Amenities</Typography>
              </Box>
            </MotionItem>

            {/* Lockers or focus stat */}
            <MotionItem>
              {showLockers ? (
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Typography variant="h2" fontWeight={800}>
                    {lockerNum !== null ? (
                      <AnimatedNumber value={lockerNum} duration={1.3} />
                    ) : (
                      library.locker_count
                    )}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.8 }}>Lockers</Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Typography variant="h2" fontWeight={800}>100%</Typography>
                  <Typography variant="h6" sx={{ opacity: 0.8 }}>Focus</Typography>
                </Box>
              )}
            </MotionItem>
          </MotionList>
        </Container>
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Reveal y={20} duration={0.6}>
              <Typography variant="overline" sx={{ color: '#EC4899', letterSpacing: 3, fontWeight: 600 }}>
                ABOUT US
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 3 }}>
                Your Perfect Study Sanctuary
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.9, fontSize: '1.1rem' }}>
                {library.description || defaultDescription}
              </Typography>

              {/* Hours / days pills */}
              {(hoursStr || daysStr) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                  {hoursStr && (
                    <Chip
                      icon={<AccessTime sx={{ color: 'white !important', fontSize: 16 }} />}
                      label={hoursStr}
                      size="small"
                      sx={{
                        background: gradient,
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: 'white' },
                      }}
                    />
                  )}
                  {daysStr && (
                    <Chip
                      icon={<CalendarToday sx={{ color: 'white !important', fontSize: 16 }} />}
                      label={daysStr}
                      size="small"
                      sx={{
                        background: 'rgba(255,255,255,0.12)',
                        color: 'white',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              )}
            </Reveal>
          </Grid>
          <Grid item xs={12} md={6}>
            <Reveal delay={0.15} y={20} duration={0.6}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                }}
              >
                {images.slice(0, 4).map((img, idx) => (
                  <Box
                    key={idx}
                    component="img"
                    src={img}
                    alt={`Gallery ${idx}`}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 3,
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.06)',
                        boxShadow: '0 12px 36px rgba(139,92,246,0.4)',
                      },
                    }}
                    onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                  />
                ))}
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Container>

      {/* Plans Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: '#8B5CF6', letterSpacing: 3, fontWeight: 600 }}>
                PRICING
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                Choose Your Plan
              </Typography>
            </Box>
          </Reveal>

          {plans.length > 0 ? (
            <MotionList
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 24,
              }}
            >
              {plans.map((plan, idx) => {
                const planName = plan.name || titleCasePlanType(plan.plan_type);
                const price = formatPrice(plan.price_cents);
                const period = planPeriodLabel(plan.plan_type);
                const discPct = plan.first_time_discount;
                const discPrice = discountedFirstPrice(plan.price_cents, discPct);
                const isPopular = idx === 1;

                return (
                  <MotionItem key={plan.id}>
                    <Card
                      sx={{
                        background: isPopular ? gradient : 'rgba(255,255,255,0.05)',
                        border: isPopular ? 'none' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 4,
                        overflow: 'visible',
                        position: 'relative',
                        transition: 'transform 0.28s ease, box-shadow 0.28s ease',
                        '&:hover': {
                          transform: 'translateY(-12px)',
                          boxShadow: isPopular
                            ? '0 20px 60px rgba(139,92,246,0.45)'
                            : '0 16px 48px rgba(139,92,246,0.25)',
                        },
                      }}
                    >
                      {isPopular && (
                        <Chip
                          label="POPULAR"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            right: 16,
                            bgcolor: '#EC4899',
                            color: 'white',
                            fontWeight: 700,
                          }}
                        />
                      )}

                      {/* Discount glow badge */}
                      {discPct > 0 && (
                        <Chip
                          label={`${discPct}% OFF first time`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: 16,
                            bgcolor: 'rgba(236,72,153,0.9)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.6rem',
                            boxShadow: '0 0 12px rgba(236,72,153,0.6)',
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        {/* Plan name */}
                        <Typography variant="h6" sx={{ color: 'white', opacity: 0.8, mb: 1, textTransform: 'uppercase', fontWeight: 700 }}>
                          {planName}
                        </Typography>

                        {/* Price */}
                        {price && (
                          <>
                            {discPct > 0 && discPrice !== null ? (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through' }}
                                >
                                  {price}
                                </Typography>
                                <Typography variant="h2" sx={{ color: 'white', fontWeight: 800 }}>
                                  {formatPrice(discPrice)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                                  first time, then {price}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="h2" sx={{ color: 'white', fontWeight: 800, mb: 2 }}>
                                {price}
                              </Typography>
                            )}
                            {period && (
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}
                              >
                                {period}
                              </Typography>
                            )}
                          </>
                        )}

                        {/* Feature rows */}
                        <Box sx={{ textAlign: 'left', mb: 3 }}>
                          {plan.duration_days && (
                            <PlanFeature icon={<CalendarToday />} color="rgba(255,255,255,0.65)">
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature icon={<Schedule />} color="rgba(255,255,255,0.65)">
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature icon={<Repeat />} color="rgba(255,255,255,0.5)">
                              Auto-renews
                            </PlanFeature>
                          )}
                        </Box>

                        {/* Description */}
                        {plan.description ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.45)',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 2,
                            }}
                          >
                            {plan.description}
                          </Typography>
                        ) : null}

                        <Button
                          fullWidth
                          variant={isPopular ? 'contained' : 'outlined'}
                          onClick={() => onBookNow(plan)}
                          sx={{
                            bgcolor: isPopular ? 'white' : 'transparent',
                            color: isPopular ? '#8B5CF6' : 'white',
                            borderColor: 'rgba(255,255,255,0.3)',
                            fontWeight: 600,
                            py: 1.5,
                            borderRadius: 50,
                            transition: 'all 0.25s',
                            '&:hover': {
                              bgcolor: isPopular ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.1)',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          Get Started
                        </Button>
                      </CardContent>
                    </Card>
                  </MotionItem>
                );
              })}
            </MotionList>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 3 }}>
                Contact us for membership details
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => onBookNow()}
                sx={{
                  background: gradient,
                  px: 5,
                  py: 2,
                  borderRadius: 50,
                }}
              >
                Get in Touch
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Amenities Section */}
      {library.amenities?.length > 0 && (
        <Box sx={{ py: 10, background: 'rgba(139,92,246,0.1)' }}>
          <Container maxWidth="lg">
            <Reveal>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, textAlign: 'center', mb: 6 }}>
                What We Offer
              </Typography>
            </Reveal>
            <MotionList
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 16,
              }}
            >
              {library.amenities.map((amenity, idx) => (
                <MotionItem key={idx}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: 3,
                      transition: 'all 0.28s ease',
                      '&:hover': {
                        background: gradient,
                        transform: 'translateY(-6px)',
                        boxShadow: '0 12px 32px rgba(139,92,246,0.35)',
                      },
                    }}
                  >
                    <Box sx={{ color: 'white', mb: 2 }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 40 } })}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      {amenity}
                    </Typography>
                  </Box>
                </MotionItem>
              ))}
            </MotionList>
          </Container>
        </Box>
      )}

      {/* Location Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Reveal y={20} duration={0.6}>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 4 }}>
                Find Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, background: gradient }}>
                    <LocationOn sx={{ color: 'white' }} />
                  </Box>
                  <Typography sx={{ color: 'white' }}>{library.address}</Typography>
                </Box>
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, background: gradient }}>
                      <Phone sx={{ color: 'white' }} />
                    </Box>
                    <Typography sx={{ color: 'white' }}>
                      <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_phone}
                      </a>
                    </Typography>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, background: gradient }}>
                      <Email sx={{ color: 'white' }} />
                    </Box>
                    <Typography sx={{ color: 'white' }}>
                      <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_email}
                      </a>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={6}>
            <Reveal delay={0.15} y={20} duration={0.6}>
              <Box sx={{ height: 300, borderRadius: 4, overflow: 'hidden' }}>
                {library.lat && library.lng ? (
                  <iframe
                    title="Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: 'rgba(255,255,255,0.05)' }}>
                    <Typography color="rgba(255,255,255,0.5)">Map unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ py: 10, background: gradient, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Reveal>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
              Ready to Level Up Your Studies?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, fontSize: '1.1rem' }}>
              Join {library.name} and experience the perfect study environment
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => onBookNow()}
              sx={{
                bgcolor: 'white',
                color: '#8B5CF6',
                fontWeight: 700,
                px: 6,
                py: 2,
                borderRadius: 50,
                fontSize: '1.1rem',
                transition: 'all 0.28s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                },
              }}
            >
              Book Now
            </Button>
          </Reveal>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#0A0A12' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
          Powered by{' '}
          <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: '#8B5CF6' }}>
            BookMyLibrary
          </a>
        </Typography>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: 'black' } }}>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default VibrantTheme;
